import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { AxiosError } from 'axios'
import type { ReactElement } from 'react'
import type { IApiError } from '~/types/api'
import type { IAppOption } from '~/types/option'
import type { IReviewTopic, IReviewTopicPayload } from '~/types/review'

import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import CreatableSelectInput from '~/components/Inputs/ReactHookForm/CreatableSelectInput'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import CustomAppOption from '~/components/SelectOptions/CustomAppOption'
import { FORM_ERRORS } from '~/const'
import useApps from '~/hooks/useApps'
import {
  useUpdateReviewTopicMutation,
  useCreateReviewTopicMutation,
  keys as reviewTopicKeys,
  useReviewsTopicsCategoriesQuery,
} from '~/queries/reviews-topics'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { RequiredYupOption, YupAppOption } from '~/utils/yup'

import './../styles.scss'

const MODAL_ID = 'manage-topic-modal'

interface FormValues {
  applications: IAppOption[]
  category: IOption | null
  description?: string
  label: string
}

const defaultCreateValues: FormValues = {
  applications: [],
  category: null,
  description: '',
  label: '',
}

const validationSchema = Yup.object().shape({
  applications: Yup.array().of(YupAppOption).required().min(1, FORM_ERRORS.AT_LEAST_ONE),
  category: RequiredYupOption.nullable().notOneOf([null], FORM_ERRORS.REQUIRED),
  description: Yup.string().trim(),
  label: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  onClose: VoidFunction
  onFailure: (errorMessage: string) => void
  onSuccess: VoidFunction
  topic?: IReviewTopic
}

const ManageTopicModalForm = ({ onClose, onFailure, onSuccess, topic }: FormProps): ReactElement => {
  /** Local state */

  const { appsOptions, isAppsLoading } = useApps()

  const defaultValues: FormValues = topic
    ? {
        applications:
          topic.applications
            ?.map(
              ({ app_key, device_type }) =>
                appsOptions.find((el) => el.appKey === app_key && el.deviceType === device_type) as IAppOption
            )
            .filter((el) => el !== undefined) || [],
        category: { label: topic.category, value: topic.category },
        description: topic.description,
        label: topic.label,
      }
    : defaultCreateValues

  const queryClient = useQueryClient()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { data: topicCategories } = useReviewsTopicsCategoriesQuery()

  const categoryOptions: IOption[] = topicCategories?.map((category) => ({ label: category, value: category })) || []

  const { mutateAsync: createReviewTopic } = useCreateReviewTopicMutation()

  const { mutateAsync: updateReviewTopic } = useUpdateReviewTopicMutation()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    try {
      const selectedApps = values.applications?.map(({ value }) => JSON.parse(value)) || []
      const isAllApps = values.applications?.length === appsOptions?.length
      const payload: IReviewTopicPayload = {
        applications: isAllApps ? [] : selectedApps,
        category: values.category?.value || '',
        description: values.description || '',
        is_all_apps: isAllApps,
        label: values.label,
      }
      if (topic) {
        await updateReviewTopic({ payload, topicId: topic.id })
      } else {
        await createReviewTopic(payload)
      }
      await queryClient.invalidateQueries({ queryKey: reviewTopicKeys.list() })
      onSuccess()
    } catch (e) {
      captureException(e)
      onFailure(getErrorMessage(e as AxiosError<IApiError>))
    }
  }

  const getErrorMessage = (error: unknown) => {
    const defaultMessage = `Topic could not be ${topic ? 'edited' : 'created'}`

    if (isAxiosError<IApiError>(error)) {
      const status = error.response?.status

      switch (status) {
        case 409:
          return 'This label already exists'
        default:
          return defaultMessage
      }
    } else {
      return defaultMessage
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          <Controller
            control={control}
            name="applications"
            render={({ field }) => (
              <MultiSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                components={{ Option: CustomAppOption }}
                error={errors.applications?.message}
                isLoading={isAppsLoading}
                label="Applications"
                options={appsOptions}
                placeholder="Select one or more applications"
                required
              />
            )}
          />
          <Controller
            control={control}
            name="category"
            render={({ field }) => (
              <CreatableSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                error={errors.category?.message}
                label="Category"
                options={categoryOptions}
                placeholder="Select or type a category name and hit enter"
                required
              />
            )}
          />
          <TextInput
            {...register('label')}
            error={errors.label?.message}
            label="Label"
            placeholder="Enter a label"
            required
          />
          <TextArea
            {...register('description')}
            error={errors.description?.message}
            label="Description"
            placeholder="Enter a description"
          />
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose} type="button">
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const ManageTopicModal = ({ isOpen, onClose, onFailure, onSuccess, topic }: ModalProps): ReactElement => {
  return (
    <Modal className="reviews__manage-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{topic ? 'Edit' : 'Create'} topic</ModalHeader>
      <ManageTopicModalForm onClose={onClose} onFailure={onFailure} onSuccess={onSuccess} topic={topic} />
    </Modal>
  )
}

export default ManageTopicModal
